import {
  Box,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import makeStyles from "@mui/styles/makeStyles";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import * as BudjectCodes from "./budjectCodes";
import service from "./../../helpers/GifService";
import { ResponsiveBar } from "@nivo/bar";
import { proposalSelector } from "../../../../store/proposalSlice";
import { useLoading } from "../../../../providers/loadingProvider";
import { GetOrdinal } from "./../../../../Common/functions";
import { yearsFields } from "../../Constants";
import InfoDialog from "./InfoDialog";

const MyResponsiveBar = (props) => {
  const { data } = props;

  return (
    <ResponsiveBar
      data={data}
      keys={[
        "Personnel",
        "Travel",
        "Equipment",
        "Services",
        "Supplies & Materials",
        "Other",
        "Overhead",
      ]}
      indexBy="year"
      margin={{ top: 5, right: 130, bottom: 50, left: 60 }}
      padding={0.3}
      layout="horizontal"
      valueScale={{ type: "linear" }}
      indexScale={{ type: "band", round: true }}
      colors={{ scheme: "blues" }}
      defs={[
        {
          id: "dots",
          type: "patternDots",
          background: "inherit",
          color: "#38bcb2",
          size: 4,
          padding: 1,
          stagger: true,
        },
        {
          id: "lines",
          type: "patternLines",
          background: "inherit",
          color: "#00FFFF",
          rotation: -45,
          lineWidth: 2,
          spacing: 7,
        },
      ]}
      fill={[
        {
          match: {
            id: "fries",
          },
          id: "dots",
        },
        {
          match: {
            id: "Salaries",
          },
          id: "lines",
        },
      ]}
      borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: "Money",
        legendPosition: "middle",
        legendOffset: 32,
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: "Year",
        legendPosition: "middle",
        legendOffset: -50,
      }}
      labelSkipWidth={12}
      labelSkipHeight={12}
      labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
      legends={[
        {
          dataFrom: "keys",
          anchor: "bottom-right",
          direction: "column",
          justify: false,
          translateX: 110,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: "left-to-right",
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: "hover",
              style: {
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
      animate={true}
      motionStiffness={90}
      motionDamping={15}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  infoIcon: {
    marginLeft: 5,
  },
  radio: {
    color: theme.palette.gif.green.color,
  },
  container: {
    width: 325,
    backgroundColor: "transparent",
  },
  table: {
    minWidth: 650,
  },
  totalTypography: {
    fontSize: 16,
    fontWeight: 600,
  },
  totalRow: {
    backgroundColor: "#D0D0D0",
  },
  checkBoxLabel: {
    fontWeight: 600,
    color: "#4B4B4B",
  },
}));

const buildOverViewItem = (itemName, budgetList, numberOfYears) => {
  if (numberOfYears === undefined)
    throw new Error(`numberOfYears is mandatory`);
  const getYearBudget = (y) =>
    budgetList.reduce((a, b) => a + +b[yearsFields[y]] || 0, 0);

  let total = 0;
  const res = { item: itemName, total: "0" };
  for (let i = 0; i < numberOfYears; i++) {
    const value = getYearBudget(i);
    const key = `year${i + 1}`;
    res[key] = value;
    total += value;
  }
  res.total = total;
  return res;
};

const buildGraphView = (overview, yearsNumber) => {
  const result = [];

  const getValue = (value) => {
    if (!value) return 0;
    return parseInt(value.replace(/,/g, ""));
  };
  const getYearValue = (key, type) => {
    return overview
      .filter((ovi) => ovi.item === type)
      .reduce((a, b) => getValue(a[key]) + +b[key], 0);
  };
  for (let i = yearsNumber - 1; i >= 0; i--) {
    const yearKey = `year${i + 1}`;
    const year = {
      year: `${GetOrdinal(i + 1)} Year`,
      Salaries: getYearValue(yearKey, "Personnel"),
      Equipment: getYearValue(yearKey, "Equipment"),
      Travel: getYearValue(yearKey, "Travel"),
      Operating: getYearValue(yearKey, "Services"),
      Supplies: getYearValue(yearKey, "Supplies Materials Services"),
      Other: getYearValue(yearKey, "Other"),
      Overhead: getYearValue(yearKey, "Overhead"),
    };
    result.push(year);
  }
  return result;
};

const bugetsnames = [
  { name: "Personnel", codes: BudjectCodes.SALARY_BUDGET_CODES },
  { name: "Equipment", codes: BudjectCodes.EQUIPMENT_BUDGET_CODES },
  { name: "Services", codes: BudjectCodes.SERVIVES_BUDGET_CODES },
  { name: "Travel", codes: BudjectCodes.TRAVEL_BUDGET_CODES },
  {
    name: "Supplies & Materials",
    codes: BudjectCodes.SUPPLIES_MATERIALS_SERVICES_BUDGET_CODES,
  },
  { name: "Other", codes: BudjectCodes.OTHER_BUDGET_CODES },
  { name: "Overhead", codes: BudjectCodes.OVERHEAD_BUDGET_CODES },
];

export default function Overview() {
  const classes = useStyles();
  const proposal = useSelector(proposalSelector);
  const loading = useLoading();
  const [viewMode, setViewMode] = useState("tableView");
  const [budgetOverView, setBudgetOverView] = useState([]);
  const [budgetGraphView, setBudgetGraphView] = useState([]);
  const [overViewTotal, setOverViewTotal] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const [infoContent, setInfoContent] = useState("");

  const tableHeaders = useMemo(() => {
    const headers = [{ label: "Item", size: 300 }];
    for (let i = 0; i < proposal.numberOfYears; i++) {
      const header = { label: `Year ${i + 1}`, size: 200 };
      headers.push(header);
    }
    headers.push({ label: "Total", size: 200 });
    return headers;
  }, [proposal]);

  const viewChanged = (event) => {
    setViewMode(event.target.value);
  };
  const showInfoDialog = useCallback(async () => {
    var pageInfo = await service.getProgramInfoByProgramId({infoField:"Overview",programType:proposal.programType});   
    setInfoContent(pageInfo);
    setShowInfo(true);
   }, [proposal.programType]);

  const buildBudgetView = useCallback(
    (budgetClauses) => {
      const overview = bugetsnames
        .map((bn) => ({
          name: bn.name,
          clause: budgetClauses.filter((bc) =>
            bn.codes.includes(bc.budgetTypeInt)
          ),
        }))
        .filter((b) => b.clause.length > 0)
        .map((b) =>
          buildOverViewItem(b.name, b.clause, proposal.numberOfYears)
        );
      setBudgetGraphView(buildGraphView(overview, proposal.numberOfYears));
      setBudgetOverView(overview);
    },
    [proposal.numberOfYears]
  );

  const getBudgetClauses = useCallback(async () => {
    const loadingId = loading.addLoading("loading budgets for proposal");
    var budgetClauses = await service.getBudgetClausesByProposalId({
      proposalId: proposal.id,
    });
    buildBudgetView(budgetClauses);

    loading.removeLoading(loadingId);
  }, [proposal.id, loading, buildBudgetView]);

  useEffect(() => {
    getBudgetClauses();
  }, [getBudgetClauses]);

  useEffect(() => {
    const getTotal = (key) => budgetOverView.reduce((a, b) => a + b[key], 0);
    setOverViewTotal({
      year1: getTotal("year1"),
      year2: getTotal("year2"),
      year3: getTotal("year3"),
      year4: getTotal("year4"),
      total: getTotal("total"),
    });
  }, [budgetOverView]);

  const yearsCells = (bov, className) => {
    const cells = [];
    for (let i = 0; i < proposal.numberOfYears; i++) {
      const cell = (
        <TableCell key={i}>
          <Typography variant="subtitle1" className={className}>
            {bov[`year${i + 1}`]}
          </Typography>
        </TableCell>
      );
      cells.push(cell);
    }
    if (proposal.numberOfYears > 2) {
      cells.push(
        <TableCell key={proposal.numberOfYears}>
          <Typography variant="subtitle1" className={className}>
            {bov.total}
          </Typography>
        </TableCell>
      );
    }
    return cells;
  };

  return (
    <>
        <InfoDialog open={showInfo} onClose={() => setShowInfo(false)} content={infoContent}></InfoDialog>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle2">Overview</Typography>
        <IconButton onClick={showInfoDialog}>
          <InfoOutlinedIcon color="primary" />
        </IconButton>
      </Box>
      <Container className={classes.container} maxWidth={false}>
        <RadioGroup
          row
          aria-label="position"
          name="position"
          defaultValue="tableView"
          onChange={viewChanged}
        >
          <FormControlLabel
            value="tableView"
            control={<Radio className={classes.radio} color="default" />}
            label={
              <Typography className={classes.checkBoxLabel}>
                Table View
              </Typography>
            }
          />
          <FormControlLabel
            value="GraphView"
            control={<Radio className={classes.radio} color="default" />}
            label={
              <Typography className={classes.checkBoxLabel}>
                Graph View
              </Typography>
            }
          />
        </RadioGroup>
      </Container>
      {viewMode === "tableView" ? (
        <Table stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.label} align="left">
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {budgetOverView.map((bov) => (
              <TableRow key={bov.item}>
                <TableCell>
                  <Typography variant="subtitle2">{bov.item}</Typography>
                </TableCell>
                {yearsCells(bov)}
              </TableRow>
            ))}
            <TableRow className={classes.totalRow}>
              <TableCell>
                <Typography
                  className={classes.totalTypography}
                  variant="subtitle2"
                >
                  Total
                </Typography>
              </TableCell>
              {yearsCells(overViewTotal, classes.totalTypography)}
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <Box height={"85%"}>
          <MyResponsiveBar data={budgetGraphView} />
        </Box>
      )}
    </>
  );
}
